import {keepPreviousData, useQuery} from '@tanstack/react-query';
import axios from 'axios';

import {algoliaAppId, algoliaApiKey} from 'constants/algolia';
import {getGeolocationByAkamai} from 'libs/utils/utils';
import {AlgoliaHitMappedType, AlgoliaRsType} from 'types/successPage';

type AlgoliaDoctorsDetailsReturnType = {
    suggestions: AlgoliaHitMappedType[];
    isLoading: boolean;
};

const emptyByDefault = (element: string | undefined | null) => element ?? '';

/**
 * AlgoliaDoctorsDetails Hook
 * @param {string} value autoComplete input debounced value
 * @return {AlgoliaDoctorsDetailsReturnType} suggestion & loading status
 */
export const useGetAlgoliaDoctorsDetails = (value: string): AlgoliaDoctorsDetailsReturnType => {
    const clientsState = getGeolocationByAkamai()[2];

    const {
        data,
        isFetching,
        isInitialLoading: isLoading,
    } = useQuery({
        enabled: value.length > 1,
        placeholderData: keepPreviousData,
        queryFn: () => {
            const indexName = process.env.APP_ENV === 'production' ? 'Doctors' : 'Dev_Doctors';

            let params: {
                query: string;
                facetFilters?: string;
                facets?: string;
            } = {
                query: value,
            };

            if (clientsState)
                params = {
                    facetFilters: `Mailing Address State:${clientsState}`,
                    facets: 'Mailing Address State',
                    query: value,
                };

            return axios.get<AlgoliaRsType>(
                `https://${algoliaAppId}-dsn.algolia.net/1/indexes/${indexName}`,
                {
                    headers: {
                        'X-Algolia-API-Key': algoliaApiKey,
                        'X-Algolia-Application-Id': algoliaAppId,
                    },
                    params,
                },
            );
        },
        queryKey: ['algoliaDrDetails', value, clientsState],
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        throwOnError: true,
    });

    return {
        isLoading: isLoading || isFetching,
        suggestions:
            data?.data.hits && data?.data.hits.length
                ? data?.data.hits.map(hit => ({
                      firstName: emptyByDefault(hit['First Name']),
                      id: hit.objectID,
                      lastName: emptyByDefault(hit['Last Name']),
                      mailingAddress1stLine: emptyByDefault(hit.Address),
                      mailingAddressCity: emptyByDefault(hit['Mailing Address City']),
                      mailingAddressFax: emptyByDefault(hit['Mailing Address Fax']),
                      mailingAddressPhone: emptyByDefault(hit['Mailing Address Phone']?.toString()),
                      mailingAddressState: emptyByDefault(hit['Mailing Address State']),
                      mailingAddressZipCode: emptyByDefault(
                          hit['Mailing Address Zip Code'].toString(),
                      ),
                      organizationName: emptyByDefault(hit['Organization Name']),
                  }))
                : [],
    };
};
